/* Mock data fixtures for development without backend. */

import type {
  TenantConfig,
  ProcessingRequest,
  EntitySearchResult,
  PaginatedResponse,
} from "./types";

export const MOCK_TENANT_CONFIG: TenantConfig = {
  tenant_id: "casadei-test",
  name: "Casadei & Pellizzaro (Test)",
  modules: ["rdi"],
  confidence_strategy: "key_entities",
  confidence_key_entities: ["impianto"],
  entity_types: [
    {
      key: "chiamante",
      label: "Chiamante",
      icon: "person",
      display_template: "{cognome} {nome}",
      has_parent: false,
      parent_entity: null,
      display_fields: ["chiamante", "telefono", "codice_edificio"],
    },
    {
      key: "edificio",
      label: "Edificio",
      icon: "apartment",
      display_template: "{descrizione} - {indirizzo}, {comune}",
      has_parent: false,
      parent_entity: null,
      display_fields: ["descrizione", "indirizzo", "comune", "provincia"],
    },
    {
      key: "impianto",
      label: "Impianto",
      icon: "engineering",
      display_template: "{tipo} - {descrizione_guasto}",
      has_parent: true,
      parent_entity: "edificio",
      display_fields: ["descrizione", "desc_tipo", "ubicazione", "codice_edificio"],
    },
  ],
};

const NOW = new Date().toISOString();
const YESTERDAY = new Date(Date.now() - 86400000).toISOString();

const MOCK_REQUESTS: ProcessingRequest[] = [
  {
    id: "req-001",
    tenant_id: "casadei-test",
    channel: "email",
    message_id: "msg-aaa-001",
    status: "needs_review",
    incoming_data: {
      sender: { name: "Paolo Strocchi", email: "studiospazzolistrocchi@gmail.com" },
      subject: "Riscaldamento non funzionante - Via Mazzini 5",
      body: "Buongiorno,\n\nsegnalo che ai sig.ri Baggio-Cantone non funziona il riscaldamento dall'edificio di Via Roma 5.\nL'impianto centralizzato sembra avere un guasto alla caldaia.\n\nCordiali saluti,\nPaolo Strocchi",
      body_html: undefined,
      channel: "email",
      received_at: NOW,
      attachments: [
        { filename: "foto_caldaia.jpg", content_type: "image/jpeg", size_bytes: 245000 },
      ],
    },
    classification: { flow_type: "rdi", sub_type: "apertura", method: "llm", confidence: 0.92, reason: "Richiesta di intervento" },
    extraction: { body_clean: "segnalo che ai sig.ri Baggio-Cantone non funziona il riscaldamento dall'edificio di Via Roma 5." },
    analyses: {
      chiamante: { nome: "Paolo", cognome: "Strocchi", email: "studiospazzolistrocchi@gmail.com", provenienza: "email_from" },
      edificio: { descrizione: "COND. BAGGIO-CANTONE", indirizzo: "Via Roma", civico: "5", comune: "Forlì" },
      impianto: { tipo: "riscaldamento", descrizione_guasto: "guasto caldaia centralizzata" },
      motivo: "Riscaldamento non funzionante",
      tipologia: "TIS",
      priorita: "bassa",
    },
    matches: [
      {
        ticket_index: 0,
        entity_matches: {
          chiamante: {
            best: {
              entity: { pk: "CHI-001", fields: { nome: "Paolo", cognome: "Strocchi", email: "studiospazzolistrocchi@gmail.com", telefono: "0543 123456" } },
              display_label: "Strocchi Paolo",
              score: 0.95,
              method: "exact",
            },
            alternatives: [
              { entity: { pk: "CHI-002", fields: { nome: "Paolo", cognome: "Strocchini", email: "p.strocchini@email.it" } }, display_label: "Strocchini Paolo", score: 0.72, method: "fuzzy" },
            ],
            needs_review: false,
          },
          edificio: {
            best: {
              entity: { pk: "EDI-042", fields: { descrizione: "COND. BAGGIO-CANTONE", indirizzo: "Via Roma", civico: "5", cap: "47121", comune: "Forlì" } },
              display_label: "COND. BAGGIO-CANTONE - Via Roma, Forlì",
              score: 0.78,
              method: "fuzzy",
            },
            alternatives: [
              { entity: { pk: "EDI-043", fields: { descrizione: "COND. BAGGIO", indirizzo: "Via Roma", civico: "7", comune: "Forlì" } }, display_label: "COND. BAGGIO - Via Roma, Forlì", score: 0.65, method: "fuzzy" },
            ],
            needs_review: true,
          },
          impianto: {
            best: {
              entity: { pk: "IMP-108", fields: { tipo: "riscaldamento", descrizione_guasto: "Caldaia centralizzata", ubicazione: "Piano -1" } },
              display_label: "riscaldamento - Caldaia centralizzata",
              score: 0.92,
              method: "learned",
            },
            alternatives: [],
            needs_review: false,
          },
        },
        overall_confidence: 0.85,
      },
    ],
    output_data: null,
    error_message: null,
    created_at: NOW,
    updated_at: NOW,
    corrections: [],
  },
  {
    id: "req-002",
    tenant_id: "casadei-test",
    channel: "email",
    message_id: "msg-bbb-002",
    status: "needs_review",
    incoming_data: {
      sender: { name: "Maria Rossi", email: "maria.rossi@condominio.it" },
      subject: "Ascensore bloccato piano 3 - Via Forlanini 12",
      body: "Buongiorno, l'ascensore si è bloccato al piano 3 nel condominio di Via Forlanini 12, Forlì. È urgente perché ci sono persone anziane.\n\nMaria Rossi",
      channel: "email",
      received_at: YESTERDAY,
    },
    classification: { flow_type: "rdi", sub_type: "apertura", method: "llm", confidence: 0.97, reason: "Intervento urgente ascensore" },
    extraction: null,
    analyses: {
      chiamante: { nome: "Maria", cognome: "Rossi", email: "maria.rossi@condominio.it" },
      edificio: { descrizione: "OSPEDALE MORGAGNI", indirizzo: "Via Forlanini", civico: "12", comune: "Forlì" },
      impianto: { tipo: "ascensore", descrizione_guasto: "bloccato al piano 3" },
      motivo: "Ascensore bloccato",
      tipologia: "TIS",
      priorita: "alta",
    },
    matches: [
      {
        ticket_index: 0,
        entity_matches: {
          chiamante: {
            best: {
              entity: { pk: "CHI-090", fields: { nome: "Maria", cognome: "Rossi", email: "maria.rossi@condominio.it" } },
              display_label: "Rossi Maria",
              score: 0.88,
              method: "fuzzy",
            },
            alternatives: [],
            needs_review: false,
          },
          edificio: {
            best: {
              entity: { pk: "EDI-015", fields: { descrizione: "OSPEDALE MORGAGNI", indirizzo: "Via Forlanini", civico: "12", comune: "Forlì" } },
              display_label: "OSPEDALE MORGAGNI - Via Forlanini, Forlì",
              score: 0.45,
              method: "vector",
            },
            alternatives: [
              { entity: { pk: "EDI-016", fields: { descrizione: "COND. FORLANINI", indirizzo: "Via Forlanini", civico: "14", comune: "Forlì" } }, display_label: "COND. FORLANINI - Via Forlanini, Forlì", score: 0.40, method: "vector" },
            ],
            needs_review: true,
          },
          impianto: {
            best: null,
            alternatives: [],
            needs_review: true,
          },
        },
        overall_confidence: 0.55,
      },
    ],
    output_data: null,
    error_message: null,
    created_at: YESTERDAY,
    updated_at: YESTERDAY,
    corrections: [],
  },
  {
    id: "req-003",
    tenant_id: "casadei-test",
    channel: "email",
    message_id: "msg-ccc-003",
    status: "done",
    incoming_data: {
      sender: { name: "Luca Bianchi", email: "luca.bianchi@pec.it" },
      subject: "Perdita acqua bagno - Condominio Emma",
      body: "Si segnala perdita acqua dal bagno dell'appartamento al secondo piano del Condominio Emma, Via Galilei 11.",
      channel: "email",
      received_at: new Date(Date.now() - 172800000).toISOString(),
    },
    classification: { flow_type: "rdi", sub_type: "apertura", method: "llm", confidence: 0.90 },
    extraction: null,
    analyses: null,
    matches: null,
    output_data: { format: "agic_rdi" },
    error_message: null,
    created_at: new Date(Date.now() - 172800000).toISOString(),
    updated_at: new Date(Date.now() - 86400000).toISOString(),
    corrections: [],
  },
  {
    id: "req-004",
    tenant_id: "casadei-test",
    channel: "email",
    message_id: "msg-ddd-004",
    status: "discarded",
    incoming_data: {
      sender: { name: "Newsletter", email: "noreply@newsletter.com" },
      subject: "Offerta speciale caldaie 2026",
      body: "Approfitta delle nostre offerte...",
      channel: "email",
      received_at: new Date(Date.now() - 259200000).toISOString(),
    },
    classification: { flow_type: "discard", method: "rule", confidence: 0.99, reason: "Newsletter/spam" },
    extraction: null,
    analyses: null,
    matches: null,
    output_data: null,
    error_message: "Scartato: email promozionale",
    created_at: new Date(Date.now() - 259200000).toISOString(),
    updated_at: new Date(Date.now() - 259200000).toISOString(),
    corrections: [],
  },
];

const MOCK_ENTITY_DB: Record<string, EntitySearchResult[]> = {
  chiamante: [
    { pk: "CHI-001", label: "Strocchi Paolo", fields: { nome: "Paolo", cognome: "Strocchi", email: "studiospazzolistrocchi@gmail.com", telefono: "0543 123456" }, score: 1 },
    { pk: "CHI-002", label: "Strocchini Paolo", fields: { nome: "Paolo", cognome: "Strocchini", email: "p.strocchini@email.it" }, score: 1 },
    { pk: "CHI-090", label: "Rossi Maria", fields: { nome: "Maria", cognome: "Rossi", email: "maria.rossi@condominio.it" }, score: 1 },
    { pk: "CHI-091", label: "Bianchi Luca", fields: { nome: "Luca", cognome: "Bianchi", email: "luca.bianchi@pec.it" }, score: 1 },
  ],
  edificio: [
    { pk: "EDI-042", label: "COND. BAGGIO-CANTONE - Via Roma, Forlì", fields: { descrizione: "COND. BAGGIO-CANTONE", indirizzo: "Via Roma", civico: "5", comune: "Forlì" }, score: 1 },
    { pk: "EDI-015", label: "OSPEDALE MORGAGNI - Via Forlanini, Forlì", fields: { descrizione: "OSPEDALE MORGAGNI", indirizzo: "Via Forlanini", civico: "12", comune: "Forlì" }, score: 1 },
    { pk: "EDI-016", label: "COND. FORLANINI - Via Forlanini, Forlì", fields: { descrizione: "COND. FORLANINI", indirizzo: "Via Forlanini", civico: "14", comune: "Forlì" }, score: 1 },
    { pk: "EDI-050", label: "COND. EMMA - Via Galilei, Forlì", fields: { descrizione: "COND. EMMA", indirizzo: "Via Galilei", civico: "11", comune: "Forlì" }, score: 1 },
  ],
  impianto: [
    { pk: "IMP-108", label: "riscaldamento - Caldaia centralizzata", fields: { tipo: "riscaldamento", descrizione_guasto: "Caldaia centralizzata", ubicazione: "Piano -1" }, score: 1 },
    { pk: "IMP-200", label: "ascensore - Cabina principale", fields: { tipo: "ascensore", descrizione_guasto: "Cabina principale", ubicazione: "Vano scala A" }, score: 1 },
    { pk: "IMP-300", label: "idraulico - Colonna montante", fields: { tipo: "idraulico", descrizione_guasto: "Colonna montante bagni", ubicazione: "Piano 2" }, score: 1 },
  ],
};

// Mock API handlers
export function mockGetTenantConfig(): TenantConfig {
  return MOCK_TENANT_CONFIG;
}

export function mockListRequests(
  status?: string,
  page = 1,
  size = 20,
): PaginatedResponse<ProcessingRequest> {
  let filtered = MOCK_REQUESTS;
  if (status) {
    filtered = filtered.filter((r) => r.status === status);
  }
  const total = filtered.length;
  const start = (page - 1) * size;
  const items = filtered.slice(start, start + size);
  return { items, total, page, size };
}

export function mockGetRequest(id: string): ProcessingRequest | null {
  return MOCK_REQUESTS.find((r) => r.id === id) ?? null;
}

export function mockSearchEntities(entityType: string, q: string): EntitySearchResult[] {
  const db = MOCK_ENTITY_DB[entityType] ?? [];
  const lower = q.toLowerCase();
  return db
    .filter((e) => e.label.toLowerCase().includes(lower) || JSON.stringify(e.fields).toLowerCase().includes(lower))
    .map((e) => ({ ...e, score: 0.8 + Math.random() * 0.2 }));
}
